import { useState, useEffect } from "react";
import { Plus, Pencil, X } from "lucide-react";
import { getEnergyCardsAPI, addEnergyCardAPI, updateEnergyCardAPI } from "../../api/miningApi";
import { toast } from "react-toastify";

const EnergyCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    isLimited: "no",
    totalSupply: "",
    haveDailyLimit: "no",
    dailyLimit: "",
    tokenPrice: "",
    energyReward: "",
  });

  useEffect(() => {
    fetchEnergyCards();
  }, []);

  const fetchEnergyCards = async () => {
    try {
      setLoading(true);
      const response = await getEnergyCardsAPI();
      if (response.success) {
        setCards(response.data || []);
      } else {
        toast.error(response.message || "Failed to fetch energy cards");
      }
    } catch (error) {
      console.error("Error fetching energy cards:", error);
      toast.error("Failed to fetch energy cards");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setCurrentCard(null);
    setFormData({
      title: "",
      isLimited: "no",
      totalSupply: "",
      haveDailyLimit: "no",
      dailyLimit: "",
      tokenPrice: "",
      energyReward: "",
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (card) => {
    setEditMode(true);
    setCurrentCard(card);
    setFormData({
      title: card.title || "",
      isLimited: card.isLimited ? "yes" : "no",
      totalSupply: card.totalSupply || "",
      haveDailyLimit: card.haveDailyLimit ? "yes" : "no",
      dailyLimit: card.dailyLimit || "",
      tokenPrice: card.tokenPrice || "",
      energyReward: card.energyReward || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentCard(null);
    setFormData({
      title: "",
      isLimited: "no",
      totalSupply: "",
      haveDailyLimit: "no",
      dailyLimit: "",
      tokenPrice: "",
      energyReward: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error("Please enter card title"); return; }
    if (!formData.tokenPrice || formData.tokenPrice <= 0) { toast.error("Please enter valid token price"); return; }
    if (!formData.energyReward || formData.energyReward <= 0) { toast.error("Please enter valid energy reward"); return; }

    const payload = {
      title: formData.title.trim(),
      isLimited: formData.isLimited === "yes",
      totalSupply: formData.isLimited === "yes" && formData.totalSupply ? parseInt(formData.totalSupply) : null,
      haveDailyLimit: formData.haveDailyLimit === "yes",
      dailyLimit: formData.haveDailyLimit === "yes" && formData.dailyLimit ? parseInt(formData.dailyLimit) : null,
      tokenPrice: parseFloat(formData.tokenPrice),
      energyReward: parseFloat(formData.energyReward),
    };

    setSubmitting(true);
    try {
      let response;
      if (editMode && currentCard) {
        response = await updateEnergyCardAPI(currentCard._id, payload);
      } else {
        response = await addEnergyCardAPI(payload);
      }
      if (response.success) {
        toast.success(response.message || (editMode ? "Energy card updated successfully" : "Energy card created successfully"));
        handleCloseModal();
        fetchEnergyCards();
      } else {
        toast.error(response.message || (editMode ? "Failed to update energy card" : "Failed to create energy card"));
      }
    } catch (error) {
      console.error("Error submitting energy card:", error);
      toast.error(editMode ? "Failed to update energy card" : "Failed to create energy card");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const labelCls = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Mining
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Energy <span className="text-gradient-ginox">Cards</span></h1>
          <p className="text-sm text-muted-foreground">Manage energy cards for mining</p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Add New Card
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ginox-green"></div>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && cards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card) => (
            <div
              key={card._id}
              className="rounded-lg border border-border/70 bg-card p-5 hover:border-border transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-base font-semibold text-foreground">{card.title}</h3>
                <button
                  onClick={() => handleOpenEditModal(card)}
                  className="rounded-md border border-border/70 bg-white/[0.02] p-1.5 text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2">
                {[
                  { label: "Token Price", value: card.tokenPrice },
                  { label: "Energy Reward", value: card.energyReward },
                  { label: "Limited Supply", value: card.isLimited ? "Yes" : "No" },
                  ...(card.isLimited ? [{ label: "Total Supply", value: card.totalSupply || "N/A" }] : []),
                  { label: "Daily Limit", value: card.haveDailyLimit ? "Yes" : "No" },
                  ...(card.haveDailyLimit ? [{ label: "Limit", value: card.dailyLimit || "N/A" }] : []),
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-mono text-foreground">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && cards.length === 0 && (
        <div className="rounded-lg border border-border/70 bg-card py-16 text-center">
          <p className="text-muted-foreground text-sm">No energy cards found. Create your first card!</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif italic text-xl text-foreground">
                {editMode ? "Edit Energy Card" : "Add New Energy Card"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="rounded-md border border-border/70 bg-white/[0.02] p-1.5 text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>Card Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={inputCls}
                  placeholder="Enter card title"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Token Price *</label>
                <input
                  type="number"
                  name="tokenPrice"
                  value={formData.tokenPrice}
                  onChange={handleInputChange}
                  className={inputCls}
                  placeholder="Enter token price"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Energy Reward *</label>
                <input
                  type="number"
                  name="energyReward"
                  value={formData.energyReward}
                  onChange={handleInputChange}
                  className={inputCls}
                  placeholder="Enter energy reward"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Is Limited Supply? *</label>
                <div className="flex gap-4">
                  {["yes", "no"].map((val) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="isLimited"
                        value={val}
                        checked={formData.isLimited === val}
                        onChange={handleInputChange}
                        className="accent-ginox-green"
                      />
                      <span className="text-sm text-foreground capitalize">{val}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.isLimited === "yes" && (
                <div>
                  <label className={labelCls}>Total Supply *</label>
                  <input
                    type="number"
                    name="totalSupply"
                    value={formData.totalSupply}
                    onChange={handleInputChange}
                    className={inputCls}
                    placeholder="Enter total supply"
                    min="1"
                    required={formData.isLimited === "yes"}
                  />
                </div>
              )}

              <div>
                <label className={labelCls}>Have Daily Limit? *</label>
                <div className="flex gap-4">
                  {["yes", "no"].map((val) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="haveDailyLimit"
                        value={val}
                        checked={formData.haveDailyLimit === val}
                        onChange={handleInputChange}
                        className="accent-ginox-green"
                      />
                      <span className="text-sm text-foreground capitalize">{val}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.haveDailyLimit === "yes" && (
                <div>
                  <label className={labelCls}>Daily Limit *</label>
                  <input
                    type="number"
                    name="dailyLimit"
                    value={formData.dailyLimit}
                    onChange={handleInputChange}
                    className={inputCls}
                    placeholder="Enter daily limit"
                    min="1"
                    required={formData.haveDailyLimit === "yes"}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  className="flex-1 rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting && (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-ginox-green"></div>
                  )}
                  {submitting ? "Processing..." : editMode ? "Update Card" : "Create Card"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergyCards;
